// 참격 리본: 가산 블렌딩 리본 메시 풀. 흰 코어(SLASH_CORE_MS) + 진홍 외곽(SLASH_TRAIL_MS).
// 시간은 영웅 시계(틱)라 히트스톱·일시정지에 멈춘다. 프레임 루프에서 객체를 만들지 않는다.
import * as THREE from 'three/webgpu';
import { abs, clamp, float, mix, sin, smoothstep, step, uniform, uv, vec3 } from 'three/tsl';
import { COMBO, PALETTE, SLASH_CORE_MS, ms } from '../shared/config';
import type { GameEvent } from '../shared/events';
import { easeOut } from '../render/util';
import { TRAIL_DISSOLVE_FROM, TRAIL_LEAD, TRAIL_SHAPES } from './action/timing';

const SEG = 40;
const POOL = 16;
/** 칼날 리본 풀: 4타 연계가 0.3초 간격이어도 표시 16틱이면 동시에 많아야 3~4개 */
const BLADE_POOL = 6;
const CORE_TICKS = Math.max(2, ms(SLASH_CORE_MS.max)); // 66ms → 4틱
/**
 * 기본 공격 칼날 리본 표시 길이(틱)의 최댓값(QA 기록용). 타별 값은 action/timing.ts TRAIL_SHAPES.
 * 2026-10-27 전투 액션 VFX: 100ms(6틱) 얇은 선은 레퍼런스(0.18~0.27초 굵은 초승달 띠)보다 작고 짧아 타격감이 약했다(docs/ACTION-VFX-REF.md).
 */
export const TRAIL_TICKS = Math.max(...TRAIL_SHAPES.map((t) => t.ticks));
/** 금빛 심 색(카엘 부색 #e8b04a, heroes.ts colors) */
const KAEL_GOLD = 0xe8b04a;

/**
 * 스킬 판정 순간(Ev.Slash b=10+id)과 각성 참격·모으기를 함께 받는 쪽(CombatFx).
 * 렌더는 Ev.Slash를 SlashFx에, 각성 이벤트를 AwakenFx에 넘기므로 렌더 배선을 바꾸지 않고 여기서 이어 준다.
 */
export interface SlashSink {
  skillSlash(e: GameEvent, now: number): void;
  awakenLine(x: number, z: number, angle: number, radius: number): void;
  awakenGather(x: number, z: number): void;
  /** 기본 공격 참격 순간(2026-10-27 전투 액션 VFX): step = comboStep(0..3), (x,y,z) 영웅 발, yaw 방향, radius 판정 반경, now 영웅 시계 */
  basicSlash(step: number, x: number, y: number, z: number, yaw: number, radius: number, now: number): void;
}

class Ribbon {
  readonly mesh: THREE.Mesh;
  readonly pos: Float32Array;
  readonly head = uniform(0);
  readonly tail = uniform(0);
  readonly core = uniform(1);
  readonly outer = uniform(1);
  readonly color = uniform(new THREE.Color(PALETTE.crimson));
  /** 흰 코어 추가 세기(각성 참격선) */
  readonly coreBoost = uniform(1);
  /** 금빛 심 색(칼날 리본) */
  readonly color2 = uniform(new THREE.Color(KAEL_GOLD));
  /** 몸통 밝기 배율(초승달 모드) */
  readonly body = uniform(1);
  /** 디졸브 진행 0..1(초승달 모드) */
  readonly dissolve = uniform(0);
  /** 디졸브 무늬 위상 */
  readonly seed = uniform(0);
  /** 디졸브 시작 비율(표시 틱 대비) */
  dissolveFrom = 1;
  /** 처음부터 지워 두는 궤적 비율(칼날 리본: 판정 훨씬 전 들어 올리는 몫은 그리지 않는다) */
  tailMin = 0;
  /** 처음부터 그려져 있는 궤적 비율(칼끝 궤적 리본: 판정 시작 전에 이미 지나간 몫) */
  h0 = 0;
  active = false;
  start = 0;
  draw = 3;
  coreEnd = 4;
  end = 6;
  /** 충격파: 크기가 s0 → s1로 커진다 */
  expand = false;
  s0 = 1;
  s1 = 1;

  /**
   * blade = true: 초승달 단면 칼날 리본(2026-10-27). 가운데가 가장 두껍고 양 끝이 뾰족, 칼자루 쪽(v=0)은 투명으로 사라지고
   * 바깥 가장자리(칼끝 쪽, v=1)에 흰 심 → 금빛 → 진홍 몸통. 끝에는 노이즈로 부서지며 사라진다.
   * 일반 투명 혼합(가산이 아니다): 밝은 모래·돌바닥에서 가산 진홍은 분홍·흰빛으로 바래 고유색이 사라졌다(PC 캡처 2026-10-27).
   * false: 이전 대칭 가산 띠(호·직선·고리·각성 참격선).
   */
  constructor(readonly blade = false) {
    const g = new THREE.BufferGeometry();
    this.pos = new Float32Array((SEG + 1) * 2 * 3);
    const uvs = new Float32Array((SEG + 1) * 2 * 2);
    for (let i = 0; i <= SEG; i++) {
      uvs[i * 4] = i / SEG; uvs[i * 4 + 1] = 0;
      uvs[i * 4 + 2] = i / SEG; uvs[i * 4 + 3] = 1;
    }
    const idx: number[] = [];
    for (let i = 0; i < SEG; i++) {
      const a = i * 2, b = a + 1, c = a + 2, d = a + 3;
      idx.push(a, c, b, b, c, d);
    }
    const pa = new THREE.BufferAttribute(this.pos, 3);
    pa.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', pa);
    g.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    g.setIndex(idx);
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);

    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: blade ? THREE.NormalBlending : THREE.AdditiveBlending });
    m.fog = false;
    const u = uv().x, v = uv().y;
    const band = abs(v.mul(2).sub(1)); // 0 중심선 → 1 가장자리
    const glow = band.oneMinus().pow(1.6);
    const coreLine = smoothstep(0.0, 0.3, band).oneMinus();
    const shown = step(u, this.head).mul(smoothstep(this.tail.sub(0.2), this.tail, u));
    const lead = smoothstep(this.head.sub(0.18), this.head, u);
    const white = coreLine.mul(2.6).add(lead.mul(1.1)).mul(this.core).mul(this.coreBoost);
    const flat = this.color.mul(glow).mul(this.outer).mul(1.9).add(vec3(1, 1, 1).mul(white)).mul(shown);
    // ── 초승달 단면(칼날 리본) ──
    // 보이는 구간(꼬리~앞머리) 안에서의 위치 → 초승달 두께(가운데 1, 양 끝 0)
    const span = this.head.sub(this.tail).max(0.05);
    const rel = clamp(u.sub(this.tail).div(span), 0, 1);
    const cres = sin(rel.mul(Math.PI)).max(0).pow(0.7);
    // 안쪽 가장자리: 두께만큼 바깥(v=1)에서 들어온 자리. 안쪽은 부드럽게 투명해진다
    const vIn = float(1).sub(cres);
    const thick = smoothstep(vIn, vIn.add(0.32), v);
    const dist = float(1).sub(v); // 0 바깥 가장자리 → 1 칼자루 쪽
    const rim = smoothstep(0.1, 0.0, dist); // 가장 바깥 흰 심
    const goldBand = smoothstep(0.42, 0.04, dist);
    const bodyK = smoothstep(0.0, 0.55, v);
    // 디졸브: 사인 격자 무늬(텍스처 없이)로 높은 곳만 남다가 사라진다. 바깥 가장자리가 가장 늦게 사라진다
    const nz = sin(u.mul(41).add(v.mul(7)).add(this.seed)).mul(sin(u.mul(17).sub(v.mul(23)).add(this.seed.mul(1.7)))).mul(0.5).add(0.5);
    const keep = smoothstep(this.dissolve, this.dissolve.add(0.22), nz.mul(0.6).add(v.mul(0.45)));
    const cresWhite = rim.mul(2.4).add(lead.mul(cres).mul(1.2)).mul(this.core).mul(this.coreBoost);
    if (blade) {
      // 칼자루 쪽 어두운 진홍 → 바깥쪽 밝은(HDR) 진홍 → 금빛 띠 → 흰 심. 진홍은 몸통 밝기·남은 시간(outer)에 따라 식는다
      const warm = this.outer.mul(0.55).add(0.45);
      const bodyCol = mix(this.color.mul(0.5), this.color.mul(2.3).mul(this.body), smoothstep(0.15, 0.8, v)).mul(warm);
      m.colorNode = bodyCol.add(this.color2.mul(goldBand).mul(1.3).mul(warm)).add(vec3(1, 1, 1).mul(cresWhite));
      m.opacityNode = clamp(bodyK.mul(0.9).mul(this.outer.mul(0.4).add(0.6)).add(rim), 0, 1).mul(thick).mul(keep).mul(shown);
    } else {
      m.colorNode = flat;
    }
    this.mesh = new THREE.Mesh(g, m);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 20;
    this.mesh.visible = false;
  }

  /** 호: 중심 (cx,cz), 높이 y, center 방향(facing 규약) 기준 ±half를 dir 방향으로 쓸고 지나간다. */
  /** 칼끝 궤적: 모델 로컬(m) 칼끝·칼날 중간 점열을 영웅 위치·방향·배율로 옮겨 리본을 만든다. */
  setTrail(tip: Float32Array, base: Float32Array, ox: number, oy: number, oz: number, yaw: number, scale: number, blade: number, reach: number): void {
    const p = this.pos;
    const c = Math.cos(yaw), s = Math.sin(yaw);
    const k0 = 1 - blade, k1 = 1 + reach;
    for (let i = 0; i <= SEG; i++) {
      const o = i * 6, k = i * 3;
      // 바깥 가장자리: 칼자루에서 칼끝 방향으로 (1 + reach)배(에너지 칼날이 칼끝 밖으로 나간다)
      const tx = base[k] + (tip[k] - base[k]) * k1, ty = base[k + 1] + (tip[k + 1] - base[k + 1]) * k1, tz = base[k + 2] + (tip[k + 2] - base[k + 2]) * k1;
      // 리본 안쪽 가장자리: 칼자루(base)에서 칼끝 쪽으로 k0만큼 옮긴 점
      const bx = base[k] + (tip[k] - base[k]) * k0, by = base[k + 1] + (tip[k + 1] - base[k + 1]) * k0, bz = base[k + 2] + (tip[k + 2] - base[k + 2]) * k0;
      p[o] = ox + scale * (bx * c + bz * s); p[o + 1] = oy + scale * by; p[o + 2] = oz + scale * (-bx * s + bz * c);
      p[o + 3] = ox + scale * (tx * c + tz * s); p[o + 4] = oy + scale * ty; p[o + 5] = oz + scale * (-tx * s + tz * c);
    }
    this.expand = false;
    this.mesh.position.set(0, 0, 0);
    this.mesh.scale.setScalar(1);
    (this.mesh.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }

  setArc(cx: number, y: number, cz: number, center: number, half: number, dir: number, rIn: number, rOut: number, tilt: number): void {
    const p = this.pos;
    const mid = (rIn + rOut) / 2, hw = (rOut - rIn) / 2;
    for (let i = 0; i <= SEG; i++) {
      const u = i / SEG;
      const ang = center + dir * (-half + 2 * half * u);
      const taper = Math.pow(Math.sin(Math.PI * u), 0.6);
      const r0 = mid - hw * taper, r1 = mid + hw * taper;
      const yy = y + tilt * (u - 0.5);
      const s = Math.sin(ang), c = Math.cos(ang);
      const o = i * 6;
      p[o] = cx + s * r0; p[o + 1] = yy; p[o + 2] = cz + c * r0;
      p[o + 3] = cx + s * r1; p[o + 4] = yy; p[o + 5] = cz + c * r1;
    }
    this.expand = false;
    this.mesh.position.set(0, 0, 0);
    this.mesh.scale.setScalar(1);
    (this.mesh.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }

  /** 직선: (x0,z0)에서 angle 방향으로 len, 폭 width. */
  setLine(x0: number, y: number, z0: number, angle: number, len: number, width: number): void {
    const p = this.pos;
    const dx = Math.sin(angle), dz = Math.cos(angle);
    const px = Math.cos(angle), pz = -Math.sin(angle);
    for (let i = 0; i <= SEG; i++) {
      const u = i / SEG;
      const hw = (width / 2) * Math.pow(Math.sin(Math.PI * u), 0.5);
      const cx = x0 + dx * len * u, cz = z0 + dz * len * u;
      const o = i * 6;
      p[o] = cx - px * hw; p[o + 1] = y; p[o + 2] = cz - pz * hw;
      p[o + 3] = cx + px * hw; p[o + 4] = y; p[o + 5] = cz + pz * hw;
    }
    this.expand = false;
    this.mesh.position.set(0, 0, 0);
    this.mesh.scale.setScalar(1);
    (this.mesh.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }

  /** 바닥 충격파 고리(단위 반경, mesh 크기로 키운다) */
  setRing(x: number, z: number, width: number, r0: number, r1: number): void {
    const p = this.pos;
    for (let i = 0; i <= SEG; i++) {
      const ang = (i / SEG) * Math.PI * 2;
      const s = Math.sin(ang), c = Math.cos(ang);
      const o = i * 6;
      p[o] = s * (1 - width); p[o + 1] = 0; p[o + 2] = c * (1 - width);
      p[o + 3] = s; p[o + 4] = 0; p[o + 5] = c;
    }
    this.expand = true;
    this.s0 = r0;
    this.s1 = r1;
    this.mesh.position.set(x, 0.08, z);
    (this.mesh.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }

  update(now: number): void {
    const t = now - this.start;
    if (t >= this.end) {
      this.active = false;
      this.mesh.visible = false;
      return;
    }
    this.mesh.visible = true;
    const draw = Math.max(1, this.draw);
    this.head.value = this.h0 + (1 - this.h0) * Math.min(1, t / draw);
    this.core.value = t <= this.coreEnd ? 1 : Math.max(0, 1 - (t - this.coreEnd) / 2);
    this.outer.value = t <= draw ? 1 : Math.max(0, 1 - (t - draw) / Math.max(1, this.end - draw));
    const tailStart = draw * 0.5;
    this.tail.value = Math.max(this.tailMin, t <= tailStart ? 0 : Math.min(1.2, ((t - tailStart) / Math.max(1, this.end - tailStart)) * 1.2));
    // 초승달 리본: 표시 틱의 dissolveFrom 이후 노이즈 디졸브(0 → 1)
    const d0 = this.end * this.dissolveFrom;
    this.dissolve.value = t <= d0 ? 0 : Math.min(1, (t - d0) / Math.max(1, this.end - d0));
    if (this.expand) {
      const s = this.s0 + (this.s1 - this.s0) * easeOut(Math.min(1, t / this.end));
      this.mesh.scale.set(s, 1, s);
      this.tail.value = 0;
    }
  }
}

export class SlashFx {
  readonly group = new THREE.Group();
  /** 스킬 판정·각성 연출을 함께 그리는 쪽(CombatFx가 생성 때 스스로 붙는다) */
  sink: SlashSink | null = null;
  private readonly pool: Ribbon[] = [];
  /** 칼날 리본(초승달, 일반 투명 혼합) 풀 */
  private readonly blades: Ribbon[] = [];
  private next = 0;
  private nextBlade = 0;

  constructor() {
    for (let i = 0; i < POOL; i++) {
      const r = new Ribbon();
      this.pool.push(r);
      this.group.add(r.mesh);
    }
    for (let i = 0; i < BLADE_POOL; i++) {
      const r = new Ribbon(true);
      this.blades.push(r);
      this.group.add(r.mesh);
    }
  }

  private take(now: number, draw: number, coreEnd: number, end: number, color: number, blade = false): Ribbon {
    const pool = blade ? this.blades : this.pool;
    let r = pool.find((x) => !x.active);
    if (!r) {
      if (blade) { r = pool[this.nextBlade]; this.nextBlade = (this.nextBlade + 1) % pool.length; }
      else { r = pool[this.next]; this.next = (this.next + 1) % pool.length; }
    }
    r.active = true;
    r.start = now;
    r.draw = draw;
    r.coreEnd = coreEnd;
    r.end = end;
    r.color.value.setHex(color);
    r.coreBoost.value = 1;
    r.body.value = 1;
    r.dissolveFrom = 1;
    r.dissolve.value = 0;
    r.tailMin = 0;
    r.h0 = 0;
    r.update(now);
    return r;
  }

  /** Ev.Slash(기본 공격)를 모델의 칼끝 궤적으로 그린다. */
  /**
   * 2026-10-27: 초승달 단면 굵은 리본(타별 모양 TRAIL_SHAPES — 칼날 72~86% + 칼끝 밖 28~50%, 11~16틱). 칼자루 쪽은 투명이라 커도 몸이 보인다.
   * step: comboStep(0..3)
   */
  onTrail(tip: Float32Array, base: Float32Array, x: number, y: number, z: number, yaw: number, scale: number, headStart: number, drawTicks: number, finisher: boolean, ringRadius: number, now: number, step = finisher ? 3 : 0): void {
    const sh = TRAIL_SHAPES[step] ?? TRAIL_SHAPES[0];
    const rb = this.take(now, Math.max(1, drawTicks), CORE_TICKS + 2, sh.ticks, PALETTE.crimson, true);
    rb.setTrail(tip, base, x, y, z, yaw, scale, sh.blade, sh.reach);
    rb.h0 = Math.min(0.95, Math.max(0, headStart));
    // 판정보다 훨씬 앞의 들어 올림 궤적(머리 위·등 뒤)은 지워 두고 판정 전후 호만 그린다: 3타는 궤적의 79%가 판정 전이라
    // 굵은 리본이 영웅 뒤 머리 위에 떠 있는 판처럼 보였다(PC 플레이 캡처 2026-10-27). 칼끝 궤적의 판정 앞 TRAIL_LEAD만 남긴다
    rb.tailMin = Math.max(0, rb.h0 - TRAIL_LEAD);
    rb.coreBoost.value = sh.core;
    rb.body.value = sh.body;
    rb.dissolveFrom = TRAIL_DISSOLVE_FROM;
    rb.seed.value = (now * 0.618) % 6.28;
    rb.color2.value.setHex(KAEL_GOLD);
    rb.update(now);
    this.sink?.basicSlash(step, x, y, z, yaw, finisher ? ringRadius / 1.05 : 0, now);
    // 마무리타(360° 판정): 바닥에 판정 반경을 가리키는 가는 고리(접촉 효과가 아니다: 명중 여부와 무관하게 휘두른 범위만 보인다)
    if (finisher) this.ring(x, z, ringRadius, PALETTE.crimson, now, 9);
  }

  /** Ev.Slash: b = comboStep(0..3) 또는 10+skillId, c 반경, d 부채꼴 각, flags&1 마무리타 */
  onSlash(e: GameEvent, now: number): void {
    const y = 1.0;
    if (e.b < 10) {
      const step = e.b | 0;
      const spec = COMBO[step] ?? COMBO[0];
      const r = e.c > 0 ? e.c : spec.range;
      const arc = e.d > 0 ? e.d : spec.arc;
      const draw = Math.min(CORE_TICKS, Math.max(2, spec.active));
      this.sink?.basicSlash(step, e.x, 0, e.z, e.a, r, now);
      const rb = this.take(now, draw, CORE_TICKS, (TRAIL_SHAPES[step] ?? TRAIL_SHAPES[0]).ticks, PALETTE.crimson);
      if (step === 3 || arc > Math.PI * 1.9) {
        rb.setArc(e.x, y * 0.8, e.z, e.a, Math.PI, 1, r * 0.55, r * 1.02, 0);
        this.ring(e.x, e.z, r * 1.05, PALETTE.crimson, now, 9);
      } else {
        const dir = step === 1 ? -1 : 1;
        const tilt = step === 0 ? 0.7 : step === 1 ? -0.7 : 1.3;
        rb.setArc(e.x, y, e.z, e.a, arc / 2, dir, r * 0.45, r * 1.0, tilt);
      }
      return;
    }
    const skill = e.b - 10;
    this.sink?.skillSlash(e, now);
    // 연결점(CombatFx)이 있으면 스킬 판정 리본은 여기서 그리지 않는다. 칼날 폭풍 회차 호(arc)·돌진 경로 균열·붉은 낙화 충격파는 CombatFx가 맡는다.
    // 2026-10-19: 돌진의 고정 6.5m 직선 리본은 벽에 막혀 짧게 멈춰도 벽 너머까지 그려져 가짜 적중처럼 보였다(COMBAT-FEEL 9.3)
    if (this.sink) return;
    if (skill === 1) {
      // 피의 돌진: 앞으로 길게 찌르는 직선
      const rb = this.take(now, 3, CORE_TICKS, 9, PALETTE.crimson);
      rb.setLine(e.x, y, e.z, e.a, 6.5, Math.max(1.2, e.c * 0.9));
    } else if (skill === 2) {
      // 칼날 폭풍: 제자리 원형 회전 참격
      const r = e.c > 0 ? e.c : 5;
      const rb = this.take(now, 4, CORE_TICKS, 8, PALETTE.crimson);
      rb.setArc(e.x, y, e.z, e.a, Math.PI, 1, r * 0.6, r * 1.0, 0);
    } else {
      // 붉은 낙화: 착지 충격파 + 원형 참격
      const r = e.c > 0 ? e.c : 4.5;
      const rb = this.take(now, 3, CORE_TICKS, 8, PALETTE.crimson);
      rb.setArc(e.x, 0.6, e.z, e.a, Math.PI, -1, r * 0.4, r * 1.0, 0);
      this.ring(e.x, e.z, r * 1.15, 0xff6a2a, now, 14);
    }
  }

  /** 각성 난무 참격: 영역을 가르는 큰 흰 직선(코어가 1~3프레임 번쩍) */
  awakenLine(x: number, z: number, angle: number, radius: number, now: number): void {
    // 2틱에 그어지고, 흰 코어는 3틱(50ms) 번쩍인 뒤 진홍 잔상이 복귀 단계까지 남는다
    // 잔상 22틱: 6타가 0.9초 안에 겹쳐 30틱이면 화면 가운데가 분홍빛으로 덮였다(dream-loop 컷인 1~2회차)
    const rb = this.take(now, 2, 3, 22, PALETTE.crimson);
    const sx = x - Math.sin(angle) * radius, sz = z - Math.cos(angle) * radius;
    // 폭 0.6m: 0.9m 띠는 블룸을 줄여도 넓고 흐린 분홍 띠로 남았다
    rb.setLine(sx, 1.1, sz, angle, radius * 2, 0.6);
    rb.coreBoost.value = 1.35;
    this.sink?.awakenLine(x, z, angle, radius);
  }

  /** 고리를 가로지르는 직선 베기(마무리타, 2026-10-27): (x0,y,z0)에서 angle 방향으로 len, 폭 width, ticks 표시 */
  crossLine(x0: number, y: number, z0: number, angle: number, len: number, width: number, now: number, ticks: number): void {
    const rb = this.take(now, 2, 4, ticks, PALETTE.crimson);
    rb.setLine(x0, y, z0, angle, len, width);
    rb.coreBoost.value = 1.2;
    rb.update(now);
  }

  /** 바닥 충격파 고리. width: 단위 반경 대비 띠 폭(0.12 = 반경의 12%) */
  ring(x: number, z: number, radius: number, color: number, now: number, ticks = 12, width = 0.12): void {
    const rb = this.take(now, 1, 2, ticks, color);
    rb.setRing(x, z, width, radius * 0.35, radius);
  }

  update(now: number): void {
    for (let i = 0; i < this.pool.length; i++) if (this.pool[i].active) this.pool[i].update(now);
    for (let i = 0; i < this.blades.length; i++) if (this.blades[i].active) this.blades[i].update(now);
  }

  /** 미리 컴파일용: 하나씩(가산 띠·칼날 리본) 보이게 둔다. */
  prewarm(on: boolean): void {
    const r = this.pool[0], b = this.blades[0];
    r.mesh.visible = on;
    b.mesh.visible = on;
    if (on) { r.setArc(0, 1, 0, 0, 1, 1, 1, 2, 0); b.setArc(0, 1, 0, 0, 1, 1, 1, 2, 0); }
  }

  clear(): void {
    for (const r of this.pool) { r.active = false; r.mesh.visible = false; }
    for (const r of this.blades) { r.active = false; r.mesh.visible = false; }
  }

  dispose(): void {
    for (const r of [...this.pool, ...this.blades]) {
      r.mesh.geometry.dispose();
      (r.mesh.material as THREE.Material).dispose();
    }
    this.pool.length = 0;
    this.blades.length = 0;
  }

  get activeCount(): number {
    let n = 0;
    for (const r of this.pool) if (r.active) n++;
    for (const r of this.blades) if (r.active) n++;
    return n;
  }
}
